import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FileDownload, Refresh } from '@mui/icons-material';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import Header from '../../Header';
import styles from './Approval.module.css';

export const ApprovalAppReport = () => {
    const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'days'));
    const [toDate, setToDate] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [selectedView, setSelectedView] = useState('requests'); // 'requests', 'customers', 'items'

    // Check if token exists on mount
    React.useEffect(() => {
        const token = localStorage.getItem('ApprovalAppToken');
        if (token) {
            setIsLoggedIn(true);
        } else {
            // Auto-login with provided credentials
            handleLogin();
        }
    }, []);

    const handleLogin = async () => {
        try {
            const response = await fetch('https://customer-onboarding.abisexport.com/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    username: 'Amitesh',
                    password: 'password123'
                })
            });

            if (response.ok) {
                const json = await response.json();
                console.log('Login Response:', json);

                if (json.success && json.token) {
                    localStorage.setItem('ApprovalAppToken', json.token);
                    localStorage.setItem('ApprovalAppUserID', json.UserID);
                    localStorage.setItem('ApprovalAppFullName', json.FullName);
                    setIsLoggedIn(true);
                } else {
                    setError('Login failed: Invalid response');
                }
            } else {
                console.error('Login Error:', response.status);
                setError(`Login failed: ${response.status}`);
            }
        } catch (err) {
            console.error('Login error:', err);
            setError(`Login error: ${err.message}`);
        }
    };

    const fetchReport = async () => {
        if (!fromDate || !toDate) {
            setError('Please select both From Date and To Date');
            return;
        }

        if (toDate.isBefore(fromDate)) {
            setError('To Date must be after From Date');
            return;
        }

        if (!isLoggedIn) {
            setError('Please login first');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const formattedFromDate = fromDate.format('YYYY-MM-DD');
            const formattedToDate = toDate.format('YYYY-MM-DD');
            const url = `https://customer-onboarding.abisexport.com/requests/approved-report?fromDate=${formattedFromDate}&toDate=${formattedToDate}`;

            console.log('Fetching URL:', url);

            const token = localStorage.getItem('ApprovalAppToken');
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const json = await response.json();
                console.log('API Response:', json);

                if (json.success) {
                    setData(json);
                } else {
                    setError('Failed to fetch report data');
                    setData(null);
                }
            } else {
                console.error('API Error:', response.status);
                setError(`Failed to fetch data: ${response.status}`);
                setData(null);
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError(`Error fetching data: ${err.message}`);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!data || !data.data || !data.data.requests || data.data.requests.length === 0) {
            alert('No data to export');
            return;
        }

        const exportData = [];
        let serialNo = 1;

        data.data.requests.forEach(request => {
            const requestDate = dayjs(request.CreatedAt).format('YYYY-MM-DD');
            const proposedBy = request.RequestedByName || '';
            const city = request.AreaName || '';
            const headerRemarks = request.HeaderRemarks || '';

            // Find HO approval remarks from Journey
            const hoApproval = request.Journey?.find(
                approval => approval.RoleID === 'ROLE_HO' || approval.RoleName === 'Head Office'
            );
            const remarksbyMaam = hoApproval?.Remarks || '';

            // Iterate through customers
            const customers = request.Customers || [];
            customers.forEach(customer => {
                const partyName = customer.CustomerName || '';
                const creditDays = customer.CreditDays || 0;
                const creditAmount = customer.CreditLimit || 0;
                const validTill = customer.Validity || '';

                // Iterate through items
                const items = customer.Items || [];
                items.forEach(item => {
                    exportData.push({
                        'S.No': serialNo++,
                        'Request ID': request.RequestID,
                        'Date': requestDate,
                        'Proposed By': proposedBy,
                        'Party Name': partyName,
                        'City': city,
                        'Credit days': creditDays,
                        'Credit amount': creditAmount,
                        'Product ID': item.ItemID || '',
                        'Product name': item.ItemName || '',
                        'Monthly Required Qty (Kg)': item.ItemCommitmentQty || 0,
                        'Expected Price': item.NewRate || 0,
                        'Current Abis-Pro Price': item.OldRate || 0,
                        'Valid till': validTill,
                        'Remarks by Proposer': headerRemarks,
                        'Margin as per expectation': item.MarginPercent ? `${item.MarginPercent}%` : '0%',
                        'Approved price': item.NewRate || 0,
                        'Remarks by Ma\'am': remarksbyMaam
                    });
                });
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Approval Report');
        XLSX.writeFile(workbook, `Approval_Report_${fromDate.format('YYYYMMDD')}_to_${toDate.format('YYYYMMDD')}.xlsx`);
    };

    // Prepare table data based on selected view
    const getTableData = () => {
        if (!data || !data.data || !data.data.requests) return [];

        if (selectedView === 'requests') {
            return data.data.requests.map(req => ({
                RequestID: req.RequestID,
                RequestedBy: req.RequestedByName,
                Area: req.AreaName,
                Branch: req.BranchName,
                Status: req.FinalStatus,
                CreatedAt: dayjs(req.CreatedAt).format('DD/MM/YYYY HH:mm'),
                ClosedAt: req.ClosedAt ? dayjs(req.ClosedAt).format('DD/MM/YYYY HH:mm') : '-',
                Remarks: req.HeaderRemarks
            }));
        } else if (selectedView === 'customers') {
            const customers = [];
            data.data.requests.forEach(req => {
                req.Customers?.forEach(cust => {
                    customers.push({
                        RequestID: req.RequestID,
                        CustomerID: cust.CustomerID,
                        CustomerName: cust.CustomerName,
                        CreditLimit: cust.CreditLimit,
                        CreditDays: cust.CreditDays,
                        ValidityStart: dayjs(cust.ValidityStart).format('DD/MM/YYYY'),
                        Validity: dayjs(cust.Validity).format('DD/MM/YYYY'),
                        TotalCommitmentQty: cust.TotalCommitmentQty
                    });
                });
            });
            return customers;
        } else if (selectedView === 'items') {
            const items = [];
            data.data.requests.forEach(req => {
                req.Customers?.forEach(cust => {
                    cust.Items?.forEach(item => {
                        items.push({
                            RequestID: req.RequestID,
                            CustomerName: cust.CustomerName,
                            ItemID: item.ItemID,
                            ItemName: item.ItemName,
                            OldRate: item.OldRate,
                            NewRate: item.NewRate,
                            DayRate: item.DayRate,
                            ItemCommitmentQty: item.ItemCommitmentQty,
                            MarginPercent: item.MarginPercent
                        });
                    });
                });
            });
            return items;
        }
        return [];
    };

    const renderTable = () => {
        const tableData = getTableData();
        if (tableData.length === 0) return null;

        if (selectedView === 'requests') {
            return (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Request ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Requested By</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Area</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Branch</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Closed At</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Remarks</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{row.RequestID}</TableCell>
                                    <TableCell>{row.RequestedBy}</TableCell>
                                    <TableCell>{row.Area}</TableCell>
                                    <TableCell>{row.Branch}</TableCell>
                                    <TableCell>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: row.Status === 'APPROVED' ? '#4caf50' : '#f44336',
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}>
                                            {row.Status}
                                        </span>
                                    </TableCell>
                                    <TableCell>{row.CreatedAt}</TableCell>
                                    <TableCell>{row.ClosedAt}</TableCell>
                                    <TableCell>{row.Remarks}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else if (selectedView === 'customers') {
            return (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Request ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Customer ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Customer Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Credit Limit</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Credit Days</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Validity Start</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Validity End</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#388e3c', color: 'white' }}>Total Commitment Qty</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{row.RequestID}</TableCell>
                                    <TableCell>{row.CustomerID}</TableCell>
                                    <TableCell>{row.CustomerName}</TableCell>
                                    <TableCell>{row.CreditLimit}</TableCell>
                                    <TableCell>{row.CreditDays}</TableCell>
                                    <TableCell>{row.ValidityStart}</TableCell>
                                    <TableCell>{row.Validity}</TableCell>
                                    <TableCell>{row.TotalCommitmentQty}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else if (selectedView === 'items') {
            return (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Request ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Customer Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Item ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Item Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Old Rate</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>New Rate</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Day Rate</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Commitment Qty</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f57c00', color: 'white' }}>Margin %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{row.RequestID}</TableCell>
                                    <TableCell>{row.CustomerName}</TableCell>
                                    <TableCell>{row.ItemID}</TableCell>
                                    <TableCell>{row.ItemName}</TableCell>
                                    <TableCell>₹{row.OldRate}</TableCell>
                                    <TableCell>₹{row.NewRate}</TableCell>
                                    <TableCell>₹{row.DayRate}</TableCell>
                                    <TableCell>{row.ItemCommitmentQty}</TableCell>
                                    <TableCell>{row.MarginPercent}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
    };

    return (
        <>
            <Header />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <Box>
                    {/* Filters Section */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="From Date"
                                            value={fromDate}
                                            onChange={(newValue) => setFromDate(newValue)}
                                            format="DD/MM/YYYY"
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="To Date"
                                            value={toDate}
                                            onChange={(newValue) => setToDate(newValue)}
                                            format="DD/MM/YYYY"
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                                        onClick={fetchReport}
                                        disabled={loading}
                                        fullWidth
                                        sx={{ height: '40px' }}
                                    >
                                        {loading ? 'Loading...' : 'Get Report'}
                                    </Button>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<FileDownload />}
                                        onClick={handleExport}
                                        disabled={loading || !data || !data.data || !data.data.requests || data.data.requests.length === 0}
                                        fullWidth
                                        sx={{ height: '40px' }}
                                    >
                                        Export to Excel
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Summary Section */}
                    {data && data.success && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                    Report Summary
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#e3f2fd',
                                                cursor: 'pointer',
                                                border: selectedView === 'requests' ? '3px solid #1976d2' : 'none',
                                                '&:hover': { transform: 'scale(1.05)', transition: 'transform 0.2s' }
                                            }}
                                            onClick={() => setSelectedView('requests')}
                                        >
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                {data.data?.totalRequests || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Requests
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#e8f5e9',
                                                cursor: 'pointer',
                                                border: selectedView === 'customers' ? '3px solid #388e3c' : 'none',
                                                '&:hover': { transform: 'scale(1.05)', transition: 'transform 0.2s' }
                                            }}
                                            onClick={() => setSelectedView('customers')}
                                        >
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                                                {data.data?.requests?.reduce((total, req) => total + (req.Customers?.length || 0), 0) || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Customers
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#fff3e0',
                                                cursor: 'pointer',
                                                border: selectedView === 'items' ? '3px solid #f57c00' : 'none',
                                                '&:hover': { transform: 'scale(1.05)', transition: 'transform 0.2s' }
                                            }}
                                            onClick={() => setSelectedView('items')}
                                        >
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                                                {data.data?.requests?.reduce((total, req) =>
                                                    total + (req.Customers?.reduce((custTotal, cust) =>
                                                        custTotal + (cust.Items?.length || 0), 0) || 0), 0) || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Items
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Data Table Section */}
                    {data && data.success && data.data && data.data.requests && data.data.requests.length > 0 && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}>
                                    {selectedView === 'requests' && 'Requests Details'}
                                    {selectedView === 'customers' && 'Customers Details'}
                                    {selectedView === 'items' && 'Items Details'}
                                </Typography>
                                {renderTable()}
                            </CardContent>
                        </Card>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    )}

                    {/* No Data State */}
                    {!loading && !data && !error && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                Select a date range and click "Get Report" to view data
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default ApprovalAppReport;
